#!/usr/bin/env node

/**
 * Electric Cloud CLI
 *
 * Entry point that wires all commands into the Commander program.
 */

import { Command } from 'commander'
import { existsSync } from 'node:fs'
import { join } from 'node:path'
import { setExplicitToken } from './auth.js'
import { setNoInput } from './lib/confirm.js'
import { setVerbose } from './client.js'
import { setQuiet } from './output.js'
import { capture, flush } from './telemetry.js'
import { getConfigDir } from './config.js'

// Auth commands
import { loginCommand } from './commands/auth/login.js'
import { logoutCommand } from './commands/auth/logout.js'
import { whoamiCommand } from './commands/auth/whoami.js'
import { tokenCreateCommand } from './commands/auth/token/create.js'
import { tokenListCommand } from './commands/auth/token/list.js'
import { tokenRevokeCommand } from './commands/auth/token/revoke.js'

// Workspace commands
import { workspacesListCommand } from './commands/workspaces/list.js'
import { workspacesGetCommand } from './commands/workspaces/get.js'

// Project commands
import { projectsListCommand } from './commands/projects/list.js'
import { projectsCreateCommand } from './commands/projects/create.js'
import { projectsGetCommand } from './commands/projects/get.js'
import { projectsUpdateCommand } from './commands/projects/update.js'
import { projectsDeleteCommand } from './commands/projects/delete.js'

// Environment commands
import { environmentsListCommand } from './commands/environments/list.js'
import { environmentsCreateCommand } from './commands/environments/create.js'
import { environmentsGetCommand } from './commands/environments/get.js'
import { environmentsUpdateCommand } from './commands/environments/update.js'
import { environmentsDeleteCommand } from './commands/environments/delete.js'

// Service commands
import { servicesListCommand } from './commands/services/list.js'
import { servicesGetCommand } from './commands/services/get.js'
import { servicesUpdateCommand } from './commands/services/update.js'
import { servicesDeleteCommand } from './commands/services/delete.js'
import { servicesGetSecretCommand } from './commands/services/get-secret.js'
import { servicesCreatePostgresCommand } from './commands/services/create/postgres.js'
import { servicesCreateStreamsCommand } from './commands/services/create/streams.js'
import { servicesCreateProxyCommand } from './commands/services/create/proxy.js'
import { servicesCreateYjsCommand } from './commands/services/create/yjs.js'

// Durable Streams archive commands
import { durableStreamsExportCommand } from './commands/durable-streams/export.js'
import { durableStreamsImportCommand } from './commands/durable-streams/import.js'

// Claim commands
import { claimCreateCommand } from './commands/claim/create.js'
import { claimStatusCommand } from './commands/claim/status.js'
import { claimCommand as claimClaimCommand } from './commands/claim/claim.js'

import { CLI_VERSION } from './client.js'
import { EXIT_VALIDATION_ERROR } from './lib/errors.js'

// Detect --json early from argv so Commander errors can be formatted as JSON.
// This runs before Commander parses, so we check process.argv directly.
const jsonMode = process.argv.includes(`--json`)

const program = new Command()
  .name(`electric`)
  .description(`CLI for Electric Cloud`)
  .version(CLI_VERSION)
  .option(`--json`, `Output as JSON`)
  .configureOutput({
    writeErr: (str) => {
      if (jsonMode) {
        // Commander prefixes errors with "error: " — strip it for the message
        const message = str.replace(/^error:\s*/i, ``).trim()
        process.stderr.write(
          JSON.stringify({
            error: `VALIDATION_ERROR`,
            message,
            exitCode: EXIT_VALIDATION_ERROR,
          }) + `\n`
        )
      } else {
        process.stderr.write(str)
      }
    },
    outputError: (str, write) => write(str),
  })
  .exitOverride((err) => {
    // commander.helpDisplayed or commander.version → exit 0
    // all other commander errors (missing arg, unknown option) → exit 4
    if (err.exitCode === 0) {
      process.exit(0)
    }
    process.exit(EXIT_VALIDATION_ERROR)
  })
  .option(`-q, --quiet`, `Output only the primary resource ID`)
  .option(`--verbose`, `Log HTTP request details to stderr`)
  .option(`--token <token>`, `Use this API token for authentication`)
  .option(`--no-input`, `Never prompt for input (fail instead)`)
  .hook(`preAction`, (thisCommand) => {
    const opts = thisCommand.opts()
    if (opts.token) {
      setExplicitToken(opts.token)
    }
    if (opts.input === false) {
      setNoInput(true)
    }
    if (opts.verbose) {
      setVerbose(true)
    }
    if (opts.quiet) {
      setQuiet(true)
    }
    // Record start time for duration tracking
    ;(program as CommandWithTelemetry).__startTime = performance.now()
  })
  .hook(`postAction`, (thisCommand, actionCommand) => {
    const startTime = (program as CommandWithTelemetry).__startTime
    const durationMs = startTime
      ? Math.round(performance.now() - startTime)
      : undefined

    const rootOpts = program.opts()
    const flagsUsed = collectFlags(rootOpts, actionCommand.opts())

    capture({
      event: `cli_command_executed`,
      properties: {
        command: getFullCommandName(actionCommand),
        cli_version: CLI_VERSION,
        auth_type: getAuthType(),
        output_format: rootOpts.json ? `json` : `text`,
        exit_code: 0,
        duration_ms: durationMs,
        flags_used: flagsUsed,
      },
    })
  })

// auth
const auth = new Command(`auth`).description(
  `Manage authentication and API tokens`
)
const token = new Command(`token`).description(`Manage API tokens`)
token.addCommand(tokenCreateCommand)
token.addCommand(tokenListCommand)
token.addCommand(tokenRevokeCommand)
auth.addCommand(loginCommand)
auth.addCommand(logoutCommand)
auth.addCommand(whoamiCommand)
auth.addCommand(token)
program.addCommand(auth)

// workspaces
const workspaces = new Command(`workspaces`).description(
  `List and inspect workspaces`
)
workspaces.addCommand(workspacesListCommand)
workspaces.addCommand(workspacesGetCommand)
program.addCommand(workspaces)

// projects
const projects = new Command(`projects`).description(
  `Manage projects within a workspace`
)
projects.addCommand(projectsListCommand)
projects.addCommand(projectsCreateCommand)
projects.addCommand(projectsGetCommand)
projects.addCommand(projectsUpdateCommand)
projects.addCommand(projectsDeleteCommand)
program.addCommand(projects)

// environments
const environments = new Command(`environments`).description(
  `Manage environments within a project`
)
environments.addCommand(environmentsListCommand)
environments.addCommand(environmentsCreateCommand)
environments.addCommand(environmentsGetCommand)
environments.addCommand(environmentsUpdateCommand)
environments.addCommand(environmentsDeleteCommand)
program.addCommand(environments)

// services
const services = new Command(`services`).description(
  `Manage services (Postgres, streams, proxy, yjs)`
)
const servicesCreate = new Command(`create`).description(`Create a new service`)
servicesCreate.addCommand(servicesCreatePostgresCommand)
servicesCreate.addCommand(servicesCreateStreamsCommand)
servicesCreate.addCommand(servicesCreateProxyCommand)
servicesCreate.addCommand(servicesCreateYjsCommand)
services.addCommand(servicesListCommand)
services.addCommand(servicesGetCommand)
services.addCommand(servicesUpdateCommand)
services.addCommand(servicesDeleteCommand)
services.addCommand(servicesGetSecretCommand)
services.addCommand(servicesCreate)
program.addCommand(services)

// durable streams archives
const durableStreams = new Command(`durable-streams`).description(
  `Export and import Durable Streams archives`
)
durableStreams.addCommand(durableStreamsExportCommand)
durableStreams.addCommand(durableStreamsImportCommand)
program.addCommand(durableStreams)

// claimable
const claimable = new Command(`claimable`).description(
  `Immediately provision a Postgres database with Sync (no auth required)`
)
claimable.addCommand(claimCreateCommand)
claimable.addCommand(claimStatusCommand)
claimable.addCommand(claimClaimCommand)
program.addCommand(claimable)

// Propagate output configuration and exit override to all subcommands
// so Commander errors (missing args, unknown flags) are JSON-formatted
// when --json is used, regardless of which subcommand triggers the error.
function propagateErrorConfig(cmd: Command) {
  for (const sub of cmd.commands) {
    sub.configureOutput(program.configureOutput())
    sub.exitOverride((err) => {
      if (err.exitCode === 0) {
        process.exit(0)
      }
      process.exit(EXIT_VALIDATION_ERROR)
    })
    propagateErrorConfig(sub)
  }
}
propagateErrorConfig(program)

// Telemetry helpers

interface CommandWithTelemetry extends Command {
  __startTime?: number
}

function getFullCommandName(cmd: Command): string {
  const parts: string[] = []
  let current: Command | null = cmd
  while (current && current !== program) {
    parts.unshift(current.name())
    current = current.parent
  }
  return parts.join(` `)
}

function getAuthType(): string {
  const opts = program.opts()
  if (opts.token) return `api_token`
  if (process.env.ELECTRIC_API_TOKEN) return `api_token`
  try {
    const authFile = join(getConfigDir(), `auth.json`)
    if (existsSync(authFile)) return `jwt`
  } catch {
    // Best-effort check
  }
  return `none`
}

function collectFlags(
  rootOpts: Record<string, unknown>,
  cmdOpts: Record<string, unknown>
): string[] {
  const flags: string[] = []
  if (rootOpts.json) flags.push(`--json`)
  if (rootOpts.quiet) flags.push(`--quiet`)
  if (rootOpts.verbose) flags.push(`--verbose`)
  if (rootOpts.token) flags.push(`--token`)
  if (rootOpts.input === false) flags.push(`--no-input`)
  if (cmdOpts.wait) flags.push(`--wait`)
  if (cmdOpts.force) flags.push(`--force`)
  return flags
}

// Run and flush telemetry before exit
program.parseAsync().then(() => flush())
